/**
 * @file        src/modals/InsuranceBenefitsModal.jsx
 * @description Every benefit of ONE TripSafe plan, grouped by the supplier's own
 *              category (Medical Covers / Travel Assistance / …), each with its
 *              sum insured and description.
 *
 *              The plan cards can only show a handful of highlights, but a policy
 *              really carries ~55 benefits — the customer cannot judge "$500,000
 *              Titanium vs $250,000 Platinum" from three bullet points. Everything
 *              here comes from the quote's `plan.benefits`; nothing is invented.
 * @author      Kinetika Labs
 * @created     2026-07-13
 * @changelog
 *   - 2026-08-18 Claude — Icon refresh: the shared glyphs (close, chevrons, check,
 *                          map pin, arrows, search, calendar) redrawn as one
 *                          consistent filled 24×24 family. Path data only — every
 *                          <svg> wrapper, class and size is unchanged.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { inr } from '../lib/format.js';

const UNCATEGORISED = 'Other benefits';

export default function InsuranceBenefitsModal({ open, onClose, payload }) {
  const plan = payload?.plan || null;
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    setQ('');
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  // Group by the supplier's category. Benefits with no category are the marketing
  // banner lines (they duplicate the card's highlights and carry no cover amount),
  // so they go last under a neutral heading rather than leading the list.
  const groups = useMemo(() => {
    const all = plan?.benefits || [];
    const needle = q.trim().toLowerCase();
    const rows = needle
      ? all.filter((b) => `${b.name} ${b.description || ''} ${b.category || ''}`.toLowerCase().includes(needle))
      : all;
    const map = new Map();
    for (const b of rows) {
      const key = b.category || UNCATEGORISED;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(b);
    }
    const entries = [...map.entries()];
    entries.sort((a, b) => (a[0] === UNCATEGORISED ? 1 : b[0] === UNCATEGORISED ? -1 : 0));
    return entries;
  }, [plan, q]);

  if (!open || !plan) return null;

  const total = (plan.benefits || []).length;
  const shown = groups.reduce((n, [, rows]) => n + rows.length, 0);

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-night/80 backdrop-blur-sm" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${plan.tier || 'Plan'} benefits`}
        className="relative w-full max-w-3xl max-h-[88vh] flex flex-col rounded-3xl border border-line bg-panel overflow-hidden"
      >
        <div className="px-6 pt-5 pb-4 border-b border-line bg-gradient-to-b from-neon/10 to-transparent">
          <div className="flex items-start gap-4">
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted">{plan.tier}</p>
              <h2 className="font-display text-2xl font-extrabold text-ink leading-tight">
                {plan.cover} <span className="text-sm font-semibold text-muted">medical cover</span>
              </h2>
              <p className="text-xs text-muted mt-1">
                {plan.insurer ? `${plan.insurer} · ` : ''}{total} benefit{total === 1 ? '' : 's'} in this policy
                {plan.indicativePremium ? ` · ${inr(plan.indicativePremium)} total` : ''}
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="ml-auto w-9 h-9 shrink-0 rounded-xl border border-line text-muted hover:text-ink hover:border-neon/60 cursor-pointer transition flex items-center justify-center"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="currentColor" d="M18.3 7.1 16.9 5.7 12 10.6 7.1 5.7 5.7 7.1l4.9 4.9-4.9 4.9 1.4 1.4 4.9-4.9 4.9 4.9 1.4-1.4-4.9-4.9z" /></svg>
            </button>
          </div>

          {/* 55+ rows is a lot to scroll — let the customer jump to "baggage". */}
          <div className="mt-4 field bg-night/70 rounded-xl border border-line px-3.5 py-2.5 flex items-center gap-2">
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-muted shrink-0"><path fill="currentColor" d="M20.9 20.1 15.7 14.9a7.2 7.2 0 1 0-1.5 1.5l5.2 5.2zM4.8 10.2a5.2 5.2 0 1 1 10.4 0 5.2 5.2 0 0 1-10.4 0" /></svg>
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search benefits — e.g. baggage, cancellation, medical"
              className="w-full bg-transparent outline-none text-[15px] placeholder:text-muted/60"
            />
            {q && <span className="text-[11px] text-muted shrink-0">{shown} of {total}</span>}
          </div>
        </div>

        <div className="overflow-y-auto nh-scroll px-6 py-5 space-y-6">
          {groups.length === 0 && (
            <p className="text-sm text-muted text-center py-8">No benefit matches “{q}”.</p>
          )}
          {groups.map(([category, rows]) => (
            <section key={category}>
              <h3 className="text-[11px] font-bold uppercase tracking-wider text-neon mb-2.5">
                {category} <span className="text-muted font-semibold">· {rows.length}</span>
              </h3>
              <div className="rounded-2xl border border-line divide-y divide-line/60">
                {rows.map((b, i) => (
                  <div key={`${b.name}-${i}`} className="px-4 py-3 flex items-start gap-3">
                    <svg viewBox="0 0 24 24" className="w-4 h-4 text-neon shrink-0 mt-0.5"><path fill="currentColor" d="M9.3 16.2 5.1 12l-1.4 1.4 5.6 5.6L20.9 7.5l-1.4-1.4z" /></svg>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-ink/90">{b.name}</p>
                      {b.description && <p className="text-xs text-muted mt-0.5 leading-relaxed">{b.description}</p>}
                    </div>
                    {/* The cover amount is the reason to read the row at all. */}
                    {b.sumInsured && (
                      <span className="shrink-0 text-[11px] font-bold text-neon bg-neon/10 border border-neon/25 rounded-lg px-2 py-1 whitespace-nowrap">
                        {b.sumInsured}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="px-6 py-4 border-t border-line bg-night/40 flex items-center justify-between gap-3">
          <p className="text-[11px] text-muted">
            Cover amounts are as published by {plan.insurer || 'the insurer'}. The policy wording is final.
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-neon to-mint text-onaccent text-sm font-bold hover:brightness-110 cursor-pointer transition shrink-0"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
